import { Injectable, APP_INITIALIZER, Inject, Optional } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  Translation,
  TRANSLOCO_LOADER,
  TranslocoLoader,
  TranslocoMissingHandler,
  TranslocoConfig,
  TRANSLOCO_MISSING_HANDLER,
  TranslocoTranspiler,
  TRANSLOCO_TRANSPILER,
  HashMap,
  DefaultTranspiler,
  TranslocoService, TranslocoFallbackStrategy, TranslocoInterceptor, TRANSLOCO_CONFIG, TRANSLOCO_FALLBACK_STRATEGY, TRANSLOCO_INTERCEPTOR, TranslateParams
} from '@ngneat/transloco';
import { from, Observable } from 'rxjs';
import { tap, filter } from 'rxjs/operators';
import { LanguageService } from '../../rulebook/lib/services/lang/language.service';

/**
 * A service for export the loader for translation library.
 *
 * @export
 * @class HttpLoaderTranslateService
 * @implements {TranslocoLoader}
 */
@Injectable({
  providedIn: 'root'
})
export class HttpLoaderTranslateService implements TranslocoLoader {

  constructor(private http: HttpClient) {
  }

  /**
   *
   *
   * {@inheritdoc}
   * @memberof HttpLoaderTranslateService
   */
  getTranslation(langPath: string) {
    const drupalSettings = window && window['drupalSettings'] ? window['drupalSettings'] : undefined;
    const drupalLang = (drupalSettings) ? drupalSettings['path']['currentLanguage'] : null;
    // Hardcode until we have a proper object.
    const drupalObjectLangAllowed = drupalSettings ? ['en', 'fr'] : null;
    const lang = drupalLang != null ? drupalLang : langPath.replace('/', '');
    const arrAllowed = (drupalObjectLangAllowed && drupalObjectLangAllowed.length > 0) ?
      drupalObjectLangAllowed : ['en', 'es', 'fr', 'sv', 'sl'];
    let setLang = '';
    // We need to force lang to english in drupal for get the translated text and use Drupal.t function.
    if (arrAllowed.indexOf(lang) > -1 && !drupalSettings) {
      setLang = arrAllowed.find(langInArr => langInArr === lang);
    } else {
      setLang = 'en';
    }
    return from(import('../../../modules/custom/wa_angular/app/assets/i18n/' + setLang + '.json')) as any;
  }
}

/**
 * Custom transpiler for avoid error when no strings are provided.
 *
 * @export
 * @class CustomTranspiler
 * @extends {DefaultTranspiler}
 * @implements {TranslocoTranspiler}
 */
export class CustomTranspiler extends DefaultTranspiler implements TranslocoTranspiler {
  transpile(value: any, params: HashMap<any>, translation: HashMap<any>) {
    let value_translated = super.transpile(value, params, translation);
    if (window && window['Drupal']) {
      value_translated = window['Drupal']['t'](value);
    }
    if (value_translated === '') {
      return value;
    } else {
      return value_translated;
    }
  }

}
@Injectable({providedIn:'root'})
/**
 * Custom transpiler for avoid error when no strings are provided.
 *
 * @export
 * @class CustomTranspiler
 * @extends {DefaultTranspiler}
 * @implements {TranslocoTranspiler}
 */
export class CustomTranslationService extends TranslocoService {
  constructor(@Optional() @Inject(TRANSLOCO_LOADER) loader: TranslocoLoader,
    @Inject(TRANSLOCO_TRANSPILER) parser: TranslocoTranspiler,
    @Inject(TRANSLOCO_MISSING_HANDLER) missingHandler: TranslocoMissingHandler,
    @Inject(TRANSLOCO_INTERCEPTOR) interceptor: TranslocoInterceptor,
    @Inject(TRANSLOCO_CONFIG) userConfig: TranslocoConfig,
    @Inject(TRANSLOCO_FALLBACK_STRATEGY) fallbackStrategy: TranslocoFallbackStrategy) {
    super(
      loader,
      parser,
      missingHandler,
      interceptor,
      userConfig,
      fallbackStrategy)
  }

  /**
   * Gets the instant translated value of a key
   *
   * @example
   *
   * translate<string>('hello')
   * translate('hello', { value: 'value' })
   * translate<string[]>(['hello', 'key'])
   * translate('hello', { }, 'en')
   * translate('scope.someKey', { }, 'en')
   */
  translate<T = any>(key: TranslateParams, params: HashMap = {}, lang = this.getActiveLang()): T {
    let value_translated = super.translate(key, params, lang);
    if (window && window['Drupal']) {
      const translation = super.translate(key, params, this.getDefaultLang());
      value_translated = window['Drupal']['t'](translation);
    }
    return value_translated;

  }

}


/**
 * Method for preload to user lang.
 *
 * @export
 * @param {LanguageService} langService The lang service of rulebook.
 * @param {TranslocoService} transloco The translation service.
 * @returns
 */
export function preloadUserLang(langService: LanguageService, transloco: TranslocoService) {
  return function () {
    return langService.lang().pipe(filter(a => a != undefined && a.LangID != undefined)).toPromise().then(({ lang }) => {
      transloco.setActiveLang(lang);
      return transloco.load(lang).toPromise();
    })
  };
}

export class CustomHandler implements TranslocoMissingHandler {
  handle(key: string, config: TranslocoConfig) {
    return '...';
  }
}

export const customMissingHandler = {
  provide: TRANSLOCO_MISSING_HANDLER,
  useClass: CustomHandler
};

